import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Plus, LayoutDashboard, Heart, LogOut, Shield, User as UserIcon, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import NotificationBell from "./NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, t, toggle } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="text-gold text-2xl font-display tracking-tight leading-none">KOILA</span>
          <span className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Holdings</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" data-testid="nav-home" className="text-sm text-white/70 hover:text-white transition-colors">{t("nav_home")}</Link>
          <Link to="/browse" data-testid="nav-browse" className="text-sm text-white/70 hover:text-white transition-colors">{t("nav_browse")}</Link>
          <Link to="/browse?category=houses" className="text-sm text-white/70 hover:text-white transition-colors">{lang === "en" ? "Houses" : "Maisons"}</Link>
          <Link to="/browse?category=cars" className="text-sm text-white/70 hover:text-white transition-colors">{lang === "en" ? "Cars" : "Voitures"}</Link>
          <Link to="/browse?category=land" className="text-sm text-white/70 hover:text-white transition-colors">{lang === "en" ? "Land" : "Terrains"}</Link>
          <Link to="/browse?category=containers" data-testid="nav-containers" className="text-sm text-white/70 hover:text-white transition-colors">{lang === "en" ? "Containers" : "Conteneurs"}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="text-xs font-medium border border-white/20 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors text-white/80"
          >
            {lang === "en" ? "FR" : "EN"}
          </button>

          <Link
            to="/post"
            data-testid="header-post-btn"
            className="flex items-center gap-2 bg-gold text-black text-sm font-medium px-3 sm:px-4 py-2 hover:bg-gold-light transition-colors"
          >
            <Plus size={16} /> <span className="whitespace-nowrap">{t("nav_post")}</span>
          </Link>

          {user ? (
            <>
              <NotificationBell />
              <div className="relative">
              <button
                data-testid="user-menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-2"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-white/20" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><UserIcon size={16} /></div>
                )}
                <ChevronDown size={14} className="text-white/50 hidden sm:block" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-[#121212] border border-white/10 z-20 py-2">
                    <div className="px-4 py-2 border-b border-white/10">
                      <p className="text-sm text-white truncate">{user.name}</p>
                      <p className="text-xs text-white/40 truncate">{user.email}</p>
                    </div>
                    <MenuItem icon={LayoutDashboard} label={t("nav_dashboard")} testid="menu-dashboard" onClick={() => { setMenuOpen(false); navigate("/dashboard"); }} />
                    <MenuItem icon={Heart} label={t("nav_favorites")} testid="menu-favorites" onClick={() => { setMenuOpen(false); navigate("/dashboard?tab=favorites"); }} />
                    <MenuItem icon={Plus} label={t("nav_post")} testid="menu-post" onClick={() => { setMenuOpen(false); navigate("/post"); }} />
                    {user.is_admin && (
                      <MenuItem icon={Shield} label="Admin Panel" testid="menu-admin" onClick={() => { setMenuOpen(false); navigate("/admin"); }} />
                    )}
                    <div className="border-t border-white/10 mt-1 pt-1">
                      <MenuItem icon={LogOut} label={t("nav_logout")} testid="menu-logout" onClick={() => { setMenuOpen(false); logout(); }} />
                    </div>
                  </div>
                </>
              )}
            </div>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin"
              className="text-sm border border-white/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors text-white/90"
            >
              {t("nav_signin")}
            </Link>
          )}

          <button className="md:hidden text-white" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-3">
          <Link to="/" data-testid="mobile-nav-home" onClick={() => setMobileOpen(false)} className="text-white/80 py-1">{t("nav_home")}</Link>
          <Link to="/browse" onClick={() => setMobileOpen(false)} className="text-white/80 py-1">{t("nav_browse")}</Link>
          <Link to="/post" onClick={() => setMobileOpen(false)} className="text-gold py-1">{t("nav_post")}</Link>
          {user && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="text-white/80 py-1">{t("nav_dashboard")}</Link>}
          {user?.is_admin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-white/80 py-1">Admin Panel</Link>}
        </div>
      )}
    </header>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors">
      <Icon size={16} className="text-white/50" /> {label}
    </button>
  );
}
